import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Tabs,
  Tab,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';

import {
  Search as SearchIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  ExpandMore as ExpandMoreIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { apiService, NewsArticle, ExternalNewsArticle } from '../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`news-tabpanel-${index}`}
      aria-labelledby={`news-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const NewsSearch: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [category, setCategory] = useState('');
  const [language, setLanguage] = useState('');
  const [source, setSource] = useState('');
  const [publishedAfter, setPublishedAfter] = useState('');
  const [publishedBefore, setPublishedBefore] = useState('');
  const [limit, setLimit] = useState(20);
  const [articles, setArticles] = useState<NewsArticle[]>([]);
  const [externalArticles, setExternalArticles] = useState<ExternalNewsArticle[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedArticle, setSelectedArticle] = useState<NewsArticle | ExternalNewsArticle | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setArticles([]);
    setExternalArticles([]);
    setSearchQuery('');
  };

  const clearFilters = () => {
    setSearchQuery('');
    setCategory('');
    setLanguage('');
    setSource('');
    setPublishedAfter('');
    setPublishedBefore('');
    setLimit(20);
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setLoading(true);
    setError('');

    try {
      if (tabValue === 0) {
        // Internal search
        const articles = await apiService.searchNews(searchQuery, limit);
        setArticles(articles);
        setExternalArticles([]);
      } else {
        // External search with advanced filters
        const searchParams: any = { query: searchQuery };
        
        // Add advanced filter parameters
        if (category) searchParams.category = category;
        if (language) searchParams.language = language;
        if (source) searchParams.source = source;
        if (publishedAfter) searchParams.published_after = publishedAfter;
        if (publishedBefore) searchParams.published_before = publishedBefore;
        if (limit) searchParams.limit = limit;
        
        console.log('NewsSearch - External search parameters:', searchParams);
        
        const response = await apiService.searchExternalNews(searchParams);
        setExternalArticles(response);
        setArticles([]);
      }
    } catch (error) {
      console.error('Error searching articles:', error);
      setError('Failed to search articles');
    } finally {
      setLoading(false);
    }
  };

  const handleIngestArticle = async (article: ExternalNewsArticle) => {
    try {
      // Note: ingestExternalNews method doesn't exist in the new API service
      // This would need to be implemented in the backend
      console.log('Ingesting article:', article);
      setError('Article ingestion not yet implemented');
    } catch (error) {
      console.error('Error ingesting article:', error);
      setError('Failed to ingest article');
    }
  };

  const handleViewArticle = (article: NewsArticle | ExternalNewsArticle) => {
    setSelectedArticle(article);
    setViewDialogOpen(true);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getSourceDisplay = (article: NewsArticle | ExternalNewsArticle): string => {
    if ('source' in article) {
      if (typeof article.source === 'object' && article.source?.name) {
        return article.source.name;
      }
      return String(article.source) || 'Unknown';
    }
    return 'Unknown';
  };

  return (
    <Box className="fade-in">
      <Box sx={{ mb: 3 }}>
        <Typography 
          variant="h5" 
          component="h1"
          sx={{ 
            fontWeight: 700,
            background: 'linear-gradient(135deg, #1e293b 0%, #475569 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: '-0.02em',
            mb: 0.5,
          }}
        >
          News Search
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
          Search and discover news articles from internal and external sources
        </Typography>
      </Box>
      
      <Tabs 
        value={tabValue} 
        onChange={handleTabChange} 
        sx={{ 
          mb: 3,
          '& .MuiTabs-indicator': {
            backgroundColor: '#3b82f6',
            height: 2,
            borderRadius: '1px 1px 0 0',
          }
        }}
      >
        <Tab 
          label="Internal Articles" 
          sx={{ 
            fontWeight: 500,
            textTransform: 'none',
            fontSize: '0.875rem',
            minHeight: 40,
          }}
        />
        <Tab 
          label="External News" 
          sx={{ 
            fontWeight: 500,
            textTransform: 'none',
            fontSize: '0.875rem',
            minHeight: 40,
          }}
        />
      </Tabs>

      {/* Search Form */}
      <Card 
        sx={{ 
          mb: 3,
          borderRadius: 2,
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          border: '1px solid #e2e8f0',
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Search Query"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Enter search terms..."
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  label="Category"
                >
                  <MenuItem value="">All Categories</MenuItem>
                  <MenuItem value="sports">Sports</MenuItem>
                  <MenuItem value="technology">Technology</MenuItem>
                  <MenuItem value="business">Business</MenuItem>
                  <MenuItem value="entertainment">Entertainment</MenuItem>
                  <MenuItem value="health">Health</MenuItem>
                  <MenuItem value="science">Science</MenuItem>
                  <MenuItem value="politics">Politics</MenuItem>
                  <MenuItem value="general">General</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Language</InputLabel>
                <Select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  label="Language"
                >
                  <MenuItem value="">Any Language</MenuItem>
                  <MenuItem value="en">English</MenuItem>
                  <MenuItem value="es">Spanish</MenuItem>
                  <MenuItem value="fr">French</MenuItem>
                  <MenuItem value="de">German</MenuItem>
                  <MenuItem value="it">Italian</MenuItem>
                  <MenuItem value="pt">Portuguese</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                disabled={loading}
                sx={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  borderRadius: 2,
                  py: 1.5,
                  boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
                    boxShadow: '0 10px 15px -3px rgba(59, 130, 246, 0.4)',
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                Search
              </Button>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={clearFilters}
                disabled={loading}
                sx={{
                  borderRadius: 2,
                  py: 1.5,
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2,
                    backgroundColor: 'grey.50',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                Clear
              </Button>
            </Grid>
          </Grid>

          {/* Advanced Filters */}
          <Accordion 
            expanded={showAdvancedFilters} 
            onChange={() => setShowAdvancedFilters(!showAdvancedFilters)}
            sx={{ mt: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">Advanced Filters</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Source"
                    value={source}
                    onChange={(e) => setSource(e.target.value)}
                    placeholder="Filter by news source"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Published After"
                    type="date"
                    value={publishedAfter}
                    onChange={(e) => setPublishedAfter(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Published Before"
                    type="date"
                    value={publishedBefore}
                    onChange={(e) => setPublishedBefore(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Results Limit</InputLabel>
                    <Select
                      value={limit}
                      onChange={(e) => setLimit(Number(e.target.value))}
                      label="Results Limit"
                    >
                      <MenuItem value={10}>10 results</MenuItem>
                      <MenuItem value={20}>20 results</MenuItem>
                      <MenuItem value={50}>50 results</MenuItem>
                      <MenuItem value={100}>100 results</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Results */}
      <TabPanel value={tabValue} index={0}>
        <Typography variant="h6" gutterBottom>
          Internal Articles ({articles.length})
        </Typography>
        
        {articles.length === 0 && !loading && (
          <Card>
            <CardContent>
              <Typography variant="body1" align="center" color="text.secondary">
                No internal articles found. Try adjusting your search criteria.
              </Typography>
            </CardContent>
          </Card>
        )}

        <Grid container spacing={2}>
          {articles.map((article) => (
            <Grid item xs={12} md={6} lg={4} key={article._id}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {article.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {article.description}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    <Chip label={article.category} size="small" />
                    <Chip label={article.source?.name || 'Unknown'} size="small" variant="outlined" />
                    <Chip label={formatDate(article.publishedAt)} size="small" variant="outlined" />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleViewArticle(article)}
                      title="View article details"
                    >
                      <ViewIcon />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Typography variant="h6" gutterBottom>
          External News ({externalArticles.length})
        </Typography>
        
        {externalArticles.length === 0 && !loading && (
          <Card>
            <CardContent>
              <Typography variant="body1" align="center" color="text.secondary">
                No external news found. Try adjusting your search criteria.
              </Typography>
            </CardContent>
          </Card>
        )}

        <Grid container spacing={2}>
          {externalArticles.map((article) => (
            <Grid item xs={12} md={6} lg={4} key={article.uuid}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {article.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {article.description}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    <Chip label={article.categories?.[0] || 'General'} size="small" />
                    <Chip label={article.source} size="small" variant="outlined" />
                    <Chip label={formatDate(article.published_at)} size="small" variant="outlined" />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleViewArticle(article)}
                      title="View article details"
                    >
                      <ViewIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="secondary"
                      onClick={() => handleIngestArticle(article)}
                      title="Ingest article to internal database"
                    >
                      <DownloadIcon />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </TabPanel>

      {/* Article Detail Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedArticle?.title}
        </DialogTitle>
        <DialogContent>
          {selectedArticle && (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {selectedArticle.description}
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                <Chip label={'categories' in selectedArticle ? (selectedArticle.categories?.[0] || 'General') : selectedArticle.category} size="small" />
                <Chip 
                  label={getSourceDisplay(selectedArticle)} 
                  size="small" 
                  variant="outlined" 
                />
                <Chip 
                  label={formatDate('publishedAt' in selectedArticle ? selectedArticle.publishedAt : selectedArticle.published_at)} 
                  size="small" 
                  variant="outlined" 
                />
              </Box>

              {('content' in selectedArticle && selectedArticle.content) && (
                <Typography variant="body2" sx={{ mb: 2 }}>
                  <strong>Content:</strong>
                </Typography>
              )}
              
              {('content' in selectedArticle && selectedArticle.content) && (
                <Typography variant="body2" sx={{ 
                  maxHeight: 300, 
                  overflowY: 'auto',
                  backgroundColor: 'grey.50',
                  p: 2,
                  borderRadius: 1
                }}>
                  {selectedArticle.content}
                </Typography>
              )}

              {('url' in selectedArticle && selectedArticle.url) && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Source URL:</strong>{' '}
                    <a href={selectedArticle.url} target="_blank" rel="noopener noreferrer">
                      {selectedArticle.url}
                    </a>
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NewsSearch;
